import {
  CoreModel,
  getNewErrors,
  InputErrorModel,
} from "@mindmemobile/mm-shared-global";
import { autorun } from "mobx";
import { CustomOptionModel } from "./CustomOptionModel";
import { ItemTypes } from "../enums";
import { RelatedItemsCategoriesModel } from "./DonationAndItemModel";
import { ItemDetailInputModel } from "./ItemDetailInputModel";
import {
  IAPICheckOutItemsModel,
  IAPICheckOutSellPageModel,
  IAPICustomOptionModel,
  IAPIRecommendItemModel,
} from "../interfaces/IAPICheckoutModel";

export class CheckOutSellPageModel extends CoreModel {
  items: CheckOutItemsModel[];
  groupByBillingFrequency: boolean;
  recommendItem: boolean;
  allowIncludedItems: boolean;
  allowRequiredItems: boolean;
  allowRecommendedItems: boolean;
  recommendedItem?: CheckOutRecommendedItemDetails | null;
  customoption: CustomOptionModel | null;
  availableItems: RelatedItemsCategoriesModel[];
  errors: InputErrorModel<CheckOutSellPageModel>[];
  constructor(data?: Partial<CheckOutSellPageModel>) {
    super();
    this.items =
      (data &&
        data.items?.map((i) => {
          return new CheckOutItemsModel(i);
        })) ||
      [];

    this.groupByBillingFrequency = data?.groupByBillingFrequency || false;
    this.recommendItem = data?.recommendItem || false;
    this.allowIncludedItems = data?.allowIncludedItems || false;
    this.allowRequiredItems = data?.allowRequiredItems || false;
    this.allowRecommendedItems = data?.allowRecommendedItems || false;
    this.availableItems =
      data?.availableItems?.map((i) => new RelatedItemsCategoriesModel(i)) ||
      [];
    this.recommendedItem = data?.recommendedItem
      ? new CheckOutRecommendedItemDetails(data?.recommendedItem)
      : null;
    this.customoption = data?.customoption
      ? new CustomOptionModel(data?.customoption)
      : null;
    this.errors = data?.errors || [];
    autorun(() => {
      this.validate(true);
    });
  }
  createAPIModel(): IAPICheckOutSellPageModel {
    return {
      items: this.items.map((i) => i.createAPIModel()),
      groupByBillingFrequency: this.groupByBillingFrequency,
      recommendItem: this.recommendItem,
      allowIncludedItems: this.allowIncludedItems,
      allowRequiredItems: this.allowRequiredItems,
      allowRecommendedItems: this.allowRecommendedItems,
      recommendedItem: this.recommendItem
        ? this.recommendedItem?.createAPIModel()
        : null,
    };
  }

  static deserializeAPIModel(
    data: IAPICheckOutSellPageModel,
    customOption?: IAPICustomOptionModel,
  ) {
    const mappedData: Partial<CheckOutSellPageModel> = {
      items: data.items.map((i) => CheckOutItemsModel.deserializeAPIModel(i)),
      groupByBillingFrequency: data.groupByBillingFrequency,
      recommendItem: data.recommendItem,
      allowIncludedItems: data.allowIncludedItems,
      allowRequiredItems: data.allowRequiredItems,
      allowRecommendedItems: data.allowRecommendedItems,
      recommendedItem:
        data.recommendedItem &&
        CheckOutRecommendedItemDetails.deserializeAPIModel(
          data.recommendedItem,
        ),
      customoption:
        customOption && CustomOptionModel.deserializeAPIModel(customOption),
    };
    return new CheckOutSellPageModel(mappedData);
  }

  hasError(key: keyof CheckOutSellPageModel) {
    return this.errors.find((f) => f.field == key) !== undefined;
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<CheckOutSellPageModel>[] = [];

    if (!this.items || this.items.length === 0) {
      this.addError("items", newErrors);
    }

    let isValidRecommendedSection = true;
    if (this.recommendItem) {
      if (this.recommendedItem) {
        isValidRecommendedSection = this.recommendedItem.validate(isAutoRun);
      }
    }
    let hasItemErrors = false;

    this.items.map((i) => {
      if (!i.validate(isAutoRun)) {
        hasItemErrors = true;
      }
    });

    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return (
      this.errors.length === 0 && !hasItemErrors && isValidRecommendedSection
    );
  }
}

export class CheckOutItemsModel extends CoreModel {
  id: string;
  itemId: string;
  priceId: string;
  itemType: keyof typeof ItemTypes | null;
  name: string;
  quantity: number;
  errors: InputErrorModel<CheckOutItemsModel>[];
  itemDetails: RelatedItemsCategoriesModel;
  isActive: boolean;
  constructor(data?: Partial<CheckOutItemsModel>) {
    super();
    this.id = (data && data.id) || "";
    this.name = (data && data.name) || "";
    this.itemId = (data && data.itemId) || "";
    this.quantity = (data && data.quantity) || 0;
    this.priceId = (data && data.priceId) || "";
    this.itemType = (data && data.itemType) || null;
    this.errors =
      (data &&
        data.errors?.map((e) => {
          return new InputErrorModel<CheckOutItemsModel>(e);
        })) ||
      [];
    this.itemDetails =
      (data && new RelatedItemsCategoriesModel(data.itemDetails)) ||
      new RelatedItemsCategoriesModel();
    this.isActive = data?.isActive ?? true;
    autorun(() => {
      this.validate(true);
    });
  }

  hasError(key: keyof CheckOutItemsModel) {
    return this.errors.find((f) => f.field == key) !== undefined;
  }

  /**
   * Whether this checkout item allows a surcharge.
   *
   * Product/service/membership rows carry the flag directly on
   * itemDetails.surcharge. Donation rows do not: the available-items API leaves
   * itemDetails.surcharge false for donations, so the real toggle has to come
   * from the donation's own detail record. That arrives either as
   * itemDetails.donationDetailPageData (checkout edit response) or, when a
   * donation is picked in the builder, as the detail CheckOutStore loads through
   * DonationService.getDonation - pass that in as donationDetail.
   */
  allowsSurcharge(donationDetail?: ItemDetailInputModel): boolean {
    if (this.itemDetails.surcharge) return true;
    if (this.itemDetails.type !== ItemTypes.Donation) return false;
    return (
      this.itemDetails.donationDetailPageData?.surcharge === true ||
      donationDetail?.surcharge === true
    );
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<CheckOutItemsModel>[] = [];

    this.validateTextField(this.name, "name", newErrors);
    this.validateNumber(this.quantity, "quantity", newErrors);
    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors.length === 0;
  }

  createAPIModel(): IAPICheckOutItemsModel {
    return {
      id: this.id,
      itemType: this.itemDetails.type,
      itemId: this.itemId,
      priceId: this.priceId,
      quantity: this.quantity,
      isActive: this.isActive,
    };
  }

  static deserializeAPIModel(data: IAPICheckOutItemsModel) {
    const mappedData: Partial<CheckOutItemsModel> = {
      id: data.id,
      itemId: data.itemId,
      priceId: data.priceId,
      quantity: data.quantity,
      itemType: data.itemDetails?.type as keyof typeof ItemTypes,
      name: data.itemDetails?.name || "",
      itemDetails:
        data.itemDetails &&
        RelatedItemsCategoriesModel.deserializeAPIModel(data.itemDetails),
      isActive: data.isActive,
    };
    return new CheckOutItemsModel(mappedData);
  }
}

export class CheckOutRecommendedItemDetails extends CoreModel {
  itemId: string;
  caption: string;
  priceId: string;
  errors: InputErrorModel<CheckOutRecommendedItemDetails>[];
  constructor(data?: Partial<CheckOutRecommendedItemDetails>) {
    super();
    this.itemId = (data && data.itemId) || "";
    this.caption = (data && data.caption) || "Recommended";
    this.priceId = (data && data.priceId) || "";
    this.errors =
      (data &&
        data.errors?.map((e) => {
          return new InputErrorModel<CheckOutRecommendedItemDetails>(e);
        })) ||
      [];
    autorun(() => {
      this.validate(true);
    });
  }

  hasError(key: keyof CheckOutRecommendedItemDetails) {
    return this.errors.find((f) => f.field == key) !== undefined;
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<CheckOutRecommendedItemDetails>[] = [];

    this.validateTextField(this.itemId, "itemId", newErrors);
    this.validateTextField(this.priceId, "itemId", newErrors);
    this.validateTextField(this.caption, "caption", newErrors);
    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }

    return this.errors.length === 0;
  }

  createAPIModel(): IAPIRecommendItemModel {
    return {
      itemId: this.itemId,
      priceId: this.priceId,
      caption: this.caption,
    };
  }

  static deserializeAPIModel(data: IAPIRecommendItemModel) {
    const mappedData: Partial<CheckOutRecommendedItemDetails> = {
      itemId: data.itemId,
      caption: data.caption,
      priceId: data.priceId,
    };
    return new CheckOutRecommendedItemDetails(mappedData);
  }
}
